import { PrimaryButton } from '../../../components/atoms/button/primaryButton/PrimaryButton';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { createProjectRun } from '../../../services/projectRunsApi';
import { UIColors } from '../../../constants/UIConstants';
import { pxToRem } from '../../../utils/getFontValue';

export interface CreateRunModalProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  projectName: string;
  onRunCreated: (runId: number) => void;
}

export function CreateRunModal({
  open,
  onClose,
  projectId,
  projectName,
  onRunCreated,
}: CreateRunModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!open || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await createProjectRun(projectId, {});
      onRunCreated(res.run_id);
    } catch (err: unknown) {
      const e = err as {
        displayMessage?: string;
        response?: { data?: { message?: string } };
      };
      setError(
        e?.displayMessage ??
          e?.response?.data?.message ??
          'Could not create the run. Check your connection and try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, _reason) => {
        if (!submitting) onClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={submitting}
      sx={{ zIndex: 2600 }}
      BackdropProps={{
        sx: { zIndex: 2599 },
      }}
      PaperProps={{
        sx: {
          borderRadius: pxToRem(8),
          zIndex: 2600,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: pxToRem(22),
          fontWeight: 700,
          color: UIColors.primary.navyBlue,
          pb: 0,
        }}
      >
        Create new run
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 1 }}>
        <Typography
          sx={{
            fontSize: pxToRem(14),
            color: UIColors.textColors.grey80,
            mb: 1,
            lineHeight: 1.5,
          }}
        >
          Start a new run for{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {projectName}
          </Box>
          ? The run will use the database settings already saved for this
          project.
        </Typography>
        <Typography
          sx={{
            fontSize: pxToRem(14),
            color: UIColors.textColors.grey80,
            lineHeight: 1.5,
          }}
        >
          Continue?
        </Typography>

        {error && (
          <Typography sx={{ color: 'error.main', mt: 2 }} role="alert">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <PrimaryButton variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </PrimaryButton>
        <PrimaryButton
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={!open || submitting}
        >
          {submitting ? 'Creating…' : 'Create run'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}
